import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import BuddySelector from "buddy/selector";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import ClientRow from "./ClientRow";
import { orderBy } from "lodash";

const allowedItemsPerPage = [20, 50, 100].map((i) => ({ value: i, label: i }));

function handleFilter(value, criteria) {
	if (criteria === "+") return !!value;
	if (criteria === "-") return !value;
	if (!criteria) return true;
	return value === criteria;
}

const AdminCompanyList = ({ organisations, statusOptions, plansList, advisors }) => {
	const [filteredStatus, setFilteredStatus] = useState(null);
	const [filteredPlan, setFilteredPlan] = useState(null);
	const [filteredAdvisor, setFilteredAdvisor] = useState(null);
	const [search, setSearch] = useState("");

	const filteredCompanyList = useMemo(() => {
		const filterByValue = (o) =>
			["id", "name"].some((k) => String(o[k])?.toLowerCase().includes(search.toLowerCase()));

		const filterResult = search.replace(/\s/g, "") ? organisations?.filter(filterByValue) : organisations;

		return filterResult.filter(({ plan, status, advisor }) => {
			const filters = [
				handleFilter(status, filteredStatus),
				handleFilter(plan, filteredPlan),
				handleFilter(advisor, filteredAdvisor),
			];

			return filters.every((f) => f);
		});
	}, [search, filteredStatus, filteredPlan, filteredAdvisor, JSON.stringify(organisations)]);

	const [itemsPerPage, setItemsPerPage] = useState(20);
	const [page, setPage] = useState(0);
	const start = page * itemsPerPage;
	const end = (page + 1) * itemsPerPage;
	const lastPage = Math.ceil(filteredCompanyList?.length / itemsPerPage);

	const Pagination = () => {
		if (filteredCompanyList?.length <= 0) return null;
		return (
			<thead>
				<tr>
					<th colSpan={7}>
						<div
							style={{
								display: "grid",
								gridGap: "1.5rem",
								grid: "1.5rem",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
							}}
						>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go To Page 1"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(0);
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 5 Pages"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(Math.max(page - 5, 0));
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 1 Page"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(page - 1);
									}}
									disabled={page === 0}
								>
									<ChevronLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<strong>
									Page {page + 1}/{lastPage}
								</strong>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title={`Go to Page ${lastPage}`}
									onClick={() => {
										if (page !== lastPage - 1) setPage(lastPage - 1);
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title="Go Forward 5 Pages"
									onClick={() => {
										if (page !== lastPage - 1) setPage(Math.min(page + 5, lastPage - 1));
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									title="Go Forward 1 Page"
									onClick={() => {
										if (page !== lastPage - 1) setPage(page + 1);
									}}
									style={{ marginRight: "0.5rem" }}
									disabled={page === lastPage - 1}
								>
									<ChevronRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
						</div>
					</th>
				</tr>
			</thead>
		);
	};

	return (
		<>
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">Organisation List</h1>
						</div>
					</div>
				</div>
			</div>
			<div
				className="content mb-20"
				style={{ overflow: "auto" }}
			>
				<div className="block-header">
					<div className="col-xs-3 pl-0">
						<label for="status">Advisor</label>
						<BuddySelector
							options={advisors}
							onChange={(e) => {
								setFilteredAdvisor(e?.value || null);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-3 pl-0">
						<label for="status">Status</label>
						<BuddySelector
							options={statusOptions}
							onChange={(e) => {
								setFilteredStatus(e?.value || null);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-3 pl-0">
						<label for="status">Plan</label>
						<BuddySelector
							options={plansList}
							onChange={(e) => {
								setFilteredPlan(e?.value || null);
							}}
							isClearable
						/>
					</div>
				</div>
				<div className="block-content">
					<div
						style={{
							border: "1px solid #bbbcbc",
							borderBottom: "none",
							position: "relative",
						}}
					>
						<MagnifyingGlassIcon
							className={"text-gray"}
							style={{
								height: "1.5em",
								position: "absolute",
								margin: "0.75em",
							}}
						></MagnifyingGlassIcon>
						<input
							name="search"
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							style={{
								border: "none",
								display: "inline-block",
								maxWidth: "100%",
								width: "100%",
								padding: 10,
								paddingLeft: 40,
							}}
						/>
					</div>
					<table
						id="table-timesheets"
						className="table-responsive table table-hover"
					>
						<Pagination />
						<thead>
							<tr>
								<th>Company</th>
								<th>Advisor</th>
								<th style={{ textAlign: "center" }}>Plan Details</th>
								<th style={{ textAlign: "center" }}>Current Status</th>
								<th style={{ textAlign: "center" }}>Created</th>
								<th width="5%" />
							</tr>
						</thead>
						<tbody>
							{filteredCompanyList?.length > 0 ? (
								filteredCompanyList?.slice(start, end)?.map((client) => (
									<ClientRow
										key={client.id}
										client={client}
									/>
								))
							) : (
								<tr>
									<th colSpan={6}>No clients{search ? ` matching "${search}"` : null}.</th>
								</tr>
							)}
						</tbody>
						<Pagination />
					</table>
					<div className="clearfix" />
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const statusOptions = [
		{ label: "No Status", value: "-", icon: "CubeTransparentIcon" },
		{ label: "Status Exists", value: "+", icon: "CubeIcon" },
		{
			label: "Statuses",
			options: [
				{ value: "Active", label: "Active", icon: "BuildingOfficeIcon" },
				{ value: "Archived", label: "Archived", icon: "ArchiveBoxIcon" },
				{ value: "Suspended", label: "Suspended", icon: "ExclamationTriangleIcon" },
				{ value: "Setup", label: "Setup", icon: "WrenchIcon" },
				{ value: "Demo", label: "Demo", icon: "StarIcon" },
				{ value: "Test", label: "Test", icon: "WrenchScrewdriverIcon" },
				{ value: "Complimentary", label: "Complimentary", icon: "GiftIcon" },
			],
		},
	];

	const plans = orderBy(state?.payment?.bundles || [], ["price"], ["desc"])
		?.filter(({ isPlan }) => !!isPlan)
		?.map(({ id, name, advisor }) => ({ value: id, label: name, advisor }));

	const plansList = [
		{ label: "No Plan", value: "-", icon: "CubeTransparentIcon" },
		{ label: "Plan Exists", value: "+", icon: "CubeIcon" },
		{
			label: "Advisor Plans",
			options: plans?.filter(({ advisor }) => advisor === "yes"),
		},
		{
			label: "Client Plans",
			options: plans?.filter(({ advisor }) => advisor === "no"),
		},
	];

	const advisorList = state.advisors?.advisorList || [];

	const advisors = [
		{ label: "No Advisory", value: "-", icon: "CubeTransparentIcon" },
		{ label: "Advisory Exists", value: "+", icon: "CubeIcon" },
		{
			label: "Advisors",
			options: advisorList.map(({ id, name }) => ({ value: id, label: name })),
		},
	];
	const organisations = state?.admin?.organisations || [];

	const advisorDict = advisorList.reduce((acc, { id, name }) => {
		acc[id] = name;
		return acc;
	}, {});

	const mappedOrganisations = organisations?.map((organisation) => {
		const advisor = advisorDict[organisation.advisor];
		return { ...organisation, advisorName: advisor };
	});

	return {
		advisors,
		organisations: mappedOrganisations,
		statusOptions,
		plansList,
	};
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(AdminCompanyList);
